import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export interface StatCardDef {
  icon: IconDefinition;
  /** Tailwind classes for the icon container, e.g. "bg-green-500 text-white" */
  iconClassName: string;
  /** Inline style override — use when CSS variables are needed (e.g. background: 'var(--theme-accent)') */
  iconStyle?: React.CSSProperties;
  label: string;
  /** Primary large value — ReactNode so caller controls color/formatting */
  value: React.ReactNode;
  /** Secondary smaller value shown below primary */
  subValue?: React.ReactNode;
}

interface UiStatsGridProps {
  cards: StatCardDef[];
  /** Show loading spinner overlay on all cards */
  loading?: boolean;
  cols?: 2 | 3 | 4 | 5;
  className?: string;
}

const COL_CLASS: Record<number, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-4',
  5: 'grid-cols-2 lg:grid-cols-5',
};

const UiStatsGrid: React.FC<UiStatsGridProps> = ({
  cards,
  loading = false,
  cols = 3,
  className = '',
}) => (
  <div className={`grid ${COL_CLASS[cols] ?? COL_CLASS[3]} gap-4 mb-6 ${className}`}>
    {cards.map((card, i) => (
      <div key={i} className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-theme flex items-center gap-4 relative">
        {loading && (
          <div className="absolute inset-0 bg-white/60 dark:bg-slate-800/60 rounded-xl flex items-center justify-center z-10">
            <div
              className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--theme-primary)', borderTopColor: 'transparent' }}
            />
          </div>
        )}
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${card.iconClassName}`}
          style={card.iconStyle}
        >
          <FontAwesomeIcon icon={card.icon} />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">{card.label}</p>
          <div className="text-md sm:text-lg font-bold leading-tight">{card.value}</div>
          {card.subValue !== undefined && (
            <div className="text-xs sm:text-sm mt-0.5">{card.subValue}</div>
          )}
        </div>
      </div>
    ))}
  </div>
);

export default UiStatsGrid;
